import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { auth, db } from '../firebase';

const THREADS_COLLECTION = 'messageThreads';

export const subscribeUserThreads = (userId, callback) => {
  const q = query(
    collection(db, THREADS_COLLECTION),
    where('participants', 'array-contains', userId)
  );

  return onSnapshot(q, (snapshot) => {
    const threads = snapshot.docs.map((threadDoc) => ({
      id: threadDoc.id,
      ...threadDoc.data()
    }));

    const sorted = threads.sort((a, b) => {
      const aTime = a.lastMessageAt?.toMillis?.() || 0;
      const bTime = b.lastMessageAt?.toMillis?.() || 0;
      return bTime - aTime;
    });

    callback(sorted);
  });
};

export const subscribeAllParentThreads = (callback) => {
  const q = query(
    collection(db, THREADS_COLLECTION),
    where('status', 'in', ['open', 'closed'])
  );

  return onSnapshot(q, (snapshot) => {
    const threads = snapshot.docs
      .map((threadDoc) => ({
        id: threadDoc.id,
        ...threadDoc.data()
      }))
      // Manager-alert threads are surfaced via subscribeManagerAlertThreads instead.
      .filter((thread) => thread.kind !== 'managerAlert');

    const sorted = threads.sort((a, b) => {
      const aTime = a.lastMessageAt?.toMillis?.() || 0;
      const bTime = b.lastMessageAt?.toMillis?.() || 0;
      return bTime - aTime;
    });

    callback(sorted);
  });
};

export const subscribeManagerAlertThreads = (userId, callback) => {
  if (!userId) {
    callback([]);
    return () => {};
  }

  const q = query(
    collection(db, THREADS_COLLECTION),
    where('kind', '==', 'managerAlert'),
    where('participants', 'array-contains', userId)
  );

  return onSnapshot(q, (snapshot) => {
    const threads = snapshot.docs.map((threadDoc) => ({
      id: threadDoc.id,
      ...threadDoc.data()
    }));

    const sorted = threads.sort((a, b) => {
      const aTime = a.lastMessageAt?.toMillis?.() || 0;
      const bTime = b.lastMessageAt?.toMillis?.() || 0;
      return bTime - aTime;
    });

    callback(sorted);
  });
};

export const subscribeThreadMessages = (threadId, callback) => {
  const q = query(
    collection(db, THREADS_COLLECTION, threadId, 'messages'),
    orderBy('createdAt', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    callback(snapshot.docs.map((messageDoc) => ({
      id: messageDoc.id,
      ...messageDoc.data()
    })));
  });
};

export const getOrCreateParentThread = async ({ parentProfile }) => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('You must be signed in.');
  }

  const existingThreadQuery = query(
    collection(db, THREADS_COLLECTION),
    where('participants', 'array-contains', currentUser.uid)
  );
  const existingThreads = await getDocs(existingThreadQuery);

  const matched = existingThreads.docs.find((threadDoc) => {
    const data = threadDoc.data();
    return data.parentId === parentProfile.parentId;
  });

  if (matched) {
    return { id: matched.id, ...matched.data() };
  }

  const participants = [currentUser.uid];
  const unreadBy = { [currentUser.uid]: 0 };

  const threadPayload = {
    parentId: parentProfile.parentId,
    parentDocId: parentProfile.id,
    parentUserId: currentUser.uid,
    parentName: `${parentProfile.firstName || ''} ${parentProfile.lastName || ''}`.trim(),
    participants,
    status: 'open',
    subject: 'Parent Support',
    unreadBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
    lastMessage: 'Conversation opened.'
  };

  const threadRef = await addDoc(collection(db, THREADS_COLLECTION), threadPayload);
  return { id: threadRef.id, ...threadPayload };
};

export const sendThreadMessage = async ({ threadId, text, sender }) => {
  if (!threadId || !text.trim()) {
    throw new Error('Thread and message are required.');
  }

  const threadRef = doc(db, THREADS_COLLECTION, threadId);
  const threadSnapshot = await getDoc(threadRef);
  const threadData = threadSnapshot.data() || {};
  let participants = threadData.participants || [];
  const unreadBy = { ...(threadData.unreadBy || {}) };

  if (!participants.includes(sender.id)) {
    participants = [...participants, sender.id];
  }

  const messagePayload = {
    text: text.trim(),
    senderId: sender.id,
    senderName: sender.name,
    senderRole: sender.role,
    createdAt: serverTimestamp()
  };

  await addDoc(collection(db, THREADS_COLLECTION, threadId, 'messages'), messagePayload);

  participants.forEach((participantId) => {
    if (participantId === sender.id) {
      unreadBy[participantId] = 0;
    } else {
      unreadBy[participantId] = (unreadBy[participantId] || 0) + 1;
    }
  });

  await updateDoc(threadRef, {
    participants,
    unreadBy,
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
    lastMessage: text.trim(),
    lastSenderId: sender.id,
    lastSenderRole: sender.role
  });
};

export const markThreadRead = async ({ threadId, userId }) => {
  const threadRef = doc(db, THREADS_COLLECTION, threadId);
  const threadSnapshot = await getDoc(threadRef);
  if (!threadSnapshot.exists()) {
    return;
  }

  const threadData = threadSnapshot.data() || {};
  const unreadBy = { ...(threadData.unreadBy || {}) };
  unreadBy[userId] = 0;

  await updateDoc(threadRef, {
    unreadBy,
    updatedAt: serverTimestamp()
  });
};

export const getOrCreateThreadByAdmin = async ({ parentDoc, adminUser }) => {
  if (!adminUser) {
    throw new Error('You must be signed in.');
  }

  const existingThreadQuery = query(
    collection(db, THREADS_COLLECTION),
    where('parentDocId', '==', parentDoc.id)
  );
  const existingThreads = await getDocs(existingThreadQuery);

  if (!existingThreads.empty) {
    const first = existingThreads.docs[0];
    return { id: first.id, ...first.data() };
  }

  const parentUserId = parentDoc.userId || null;
  const participants = [adminUser.uid];
  if (parentUserId && parentUserId !== adminUser.uid) {
    participants.push(parentUserId);
  }

  const unreadBy = participants.reduce((acc, userId) => {
    acc[userId] = 0;
    return acc;
  }, {});

  const threadPayload = {
    parentId: parentDoc.parentId,
    parentDocId: parentDoc.id,
    parentUserId: parentUserId,
    parentName: `${parentDoc.firstName || ''} ${parentDoc.lastName || ''}`.trim(),
    participants,
    status: 'open',
    subject: 'Parent Support',
    unreadBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
    lastMessage: 'Conversation opened by staff.'
  };

  const threadRef = await addDoc(collection(db, THREADS_COLLECTION), threadPayload);
  return { id: threadRef.id, ...threadPayload };
};

export const getParentsList = async () => {
  const snapshot = await getDocs(collection(db, 'parents'));
  return snapshot.docs.map((parentDoc) => ({
    id: parentDoc.id,
    ...parentDoc.data()
  }));
};

export const closeThread = async ({ threadId }) => {
  const threadRef = doc(db, THREADS_COLLECTION, threadId);
  await updateDoc(threadRef, {
    status: 'closed',
    updatedAt: serverTimestamp()
  });
};
